import { useTranslation } from 'react-i18next'

import { Receipt } from '@/components/receipts/Receipt'
import type { ReceiptVM } from '@/lib/receipts'
import type {
  CascadeCarryover,
  CascadeConflict,
} from '@/components/dashboard/agentic/types'

export type DockReceipt = {
  vm: ReceiptVM
  /** Fast-lane advisories (§4.3 "propose, human disposes") — carried
      over verbatim from the executed banner so no signal is lost. */
  conflicts: CascadeConflict[]
  carryover: CascadeCarryover[]
}

/**
 * Receipts drop UNDER the composer, inside the bottom stack (DESIGN.md
 * §4.3 spatial contract: staged work lives IN the composer; finished
 * work falls out below it). Transient by design — the durable log is
 * Activity (§5); the host clears on navigation and caps the stack so
 * the dock never dominates a 375px viewport.
 */
export function ReceiptsHost({
  items,
  onUndone,
}: {
  items: DockReceipt[]
  onUndone: (vm: ReceiptVM) => void
}) {
  const { t } = useTranslation('dashboard')

  if (items.length === 0) return null

  return (
    <div
      aria-live="polite"
      className="mx-auto flex w-full max-w-2xl flex-col gap-2 px-3 pb-2"
    >
      {items.map((item) => (
        <div key={item.vm.requestId ?? item.vm.createdAtIso}>
          <Receipt vm={item.vm} surface="dock" onUndone={onUndone} />
          {item.conflicts.length > 0 ? (
            <div className="mt-1 px-3.5 text-[13px] text-attention-text">
              {t('agentic.success.conflict_warning', {
                count: item.conflicts.length,
              })}
            </div>
          ) : null}
          {item.carryover.length > 0 ? (
            <div className="mt-1 rounded-lg bg-attention-soft px-3.5 py-1 text-[13px] text-attention-text">
              <span className="font-[650]">
                {t('agentic.success.carryover_label')}:
              </span>{' '}
              {item.carryover.map((co) => co.body).join(' · ')}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  )
}
